import { useMemo, useState } from 'react';
import { useAgreementListApi } from '@hero/api-hooks';
import type { Agreement, Client, PartyRole } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useCurrentUser } from '@hero/hooks-shared';
import { AgreementSummaryCard } from '../../../Agreement/Card/AgreementSummaryCard';
import { NoAgreementCallout } from '../../../Agreement/Card/NoAgreementCallout';
import { AgreementDrawer } from '../../../Agreement/Drawer/AgreementDrawer';
import { AgreementEditDrawer } from '../../../Agreement/Drawer/AgreementEditDrawer';

export interface AgreementInfoSectionProps {
  client: Client;
}

/**
 * Workspace Info-tab agreement section. Renders the contract envelope
 * for the per-client `(org, client)` pair directly — no `SectionCard`
 * wrapper, because the inner `AgreementSummaryCard` already has its
 * own title ("New Agreement") and status badge ("New"/"Active"), so
 * an outer "Agreement / Complete" header was visually redundant and
 * stole vertical space from a tab where the user wants the
 * contract front-and-center.
 *
 * States:
 *   - One open agreement (`status ∈ {new, active}`) →
 *     `AgreementSummaryCard` showing dates, auto-renew, monthly
 *     volume, and inline `Invoice: …` / `Pay: …` chips. Click opens
 *     the full `AgreementDrawer`.
 *   - Zero open agreements → `NoAgreementCallout` with a "Create
 *     agreement" CTA wired into `AgreementEditDrawer` with the
 *     counterparty pre-filled and locked (cannot re-aim at someone
 *     else from the workspace).
 *   - Loading → a thin skeleton placeholder so the layout below
 *     doesn't jump when the query settles.
 *
 * Backend invariant — `AgreementService.createNewAgreementAPI`
 * enforces "one open contract per (org, counterparty) pair" via
 * `findActiveOrDraftBetween`, so the list collapses to zero-or-one
 * here. If we ever lift that to N, swap the single
 * `AgreementSummaryCard` for a horizontal carousel.
 *
 * Lives in the Info tab (not Pricing) because the contract envelope
 * — payment terms, dates, monthly volume — reads alongside primary
 * contact / legal / order defaults rather than crammed above the
 * dense pricing sheet.
 */
export function AgreementInfoSection({ client }: AgreementInfoSectionProps) {
  const { orgId } = useCurrentUser();

  // Single index seek on `tenants.id`; backend returns at most one
  // open agreement for this `(org, client)` pair (see invariant above).
  const agreementsListQuery = useAgreementListApi({ partyId: client._id });
  const openAgreement = useMemo<Agreement | null>(() => {
    const list = agreementsListQuery.data ?? [];
    return list.find(a => a.status === 'new' || a.status === 'active') ?? null;
  }, [agreementsListQuery.data]);

  const counterpartyRoles: PartyRole[] | undefined =
    Array.isArray(client.roles) && client.roles.length > 0
      ? (client.roles as PartyRole[])
      : undefined;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [drawerAgreement, setDrawerAgreement] = useState<Agreement | null>(null);

  const isLoading = agreementsListQuery.isLoading;

  return (
    <>
      {isLoading ? (
        <div className="h-20 animate-pulse rounded-lg bg-gray-100" />
      ) : openAgreement ? (
        <AgreementSummaryCard
          agreement={openAgreement}
          fallbackName={client.name}
          orgId={orgId}
          onClick={() => setDrawerAgreement(openAgreement)}
        />
      ) : (
        <NoAgreementCallout
          clientName={client.name}
          onCreate={() => setIsCreateOpen(true)}
        />
      )}

      {/* Create drawer — only mounted when no open agreement exists.
          Backend would reject a duplicate via
          `agreement_org_already_has_one`, but keeping the drawer off
          the tree when the CTA isn't visible also avoids stale
          defaults if the user creates an agreement, dismisses, and
          returns to the page. */}
      {!openAgreement && (
        <AgreementEditDrawer
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          defaults={{
            counterpartyId: String(getObjectId(client._id)),
            counterpartyName: client.name,
            counterpartyRoles,
          }}
          lockedFields={['counterparty']}
          displayFor="crm"
        />
      )}

      <AgreementDrawer
        agreement={drawerAgreement}
        isOpen={!!drawerAgreement}
        onClose={() => setDrawerAgreement(null)}
      />
    </>
  );
}
